/**
 * Upload (§13.4): accepted types/size stated up front, progress, malware-scan state, a non-drag path (plain
 * file input + button), and "replace" for a rejected file. The kit never reads ID images itself — it hands the
 * File to the app, which uploads to the storage adapter (§17.3).
 */
import { useId, useRef, useState, type DragEvent } from 'react';
import { cn } from '../cn';
import { Button } from './Button';

export type UploadState = 'idle' | 'uploading' | 'scanning' | 'clean' | 'rejected' | 'failed';

export interface UploadProps {
  label: string;
  /** MIME types, e.g. ['image/jpeg','image/png','application/pdf']. */
  accept: string[];
  maxBytes: number;
  state: UploadState;
  /** 0–100 while uploading. */
  progress?: number;
  /** Plain-language reason for rejected/failed states (participant-safe). */
  reason?: string;
  fileName?: string;
  onFile: (file: File) => void;
  onRemove?: () => void;
  hint?: string;
  className?: string;
}

const STATE_TEXT: Record<UploadState, string> = {
  idle: 'No file chosen',
  uploading: 'Uploading',
  scanning: 'Checking the file for safety',
  clean: 'File received and checked',
  rejected: 'File not accepted',
  failed: 'Upload failed',
};

export function describeAccept(accept: string[]): string {
  return accept.map((t) => t.split('/')[1]?.toUpperCase() ?? t).join(', ');
}

export function formatBytes(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)} MB`;
  if (n >= 1_000) return `${Math.round(n / 1_000)} KB`;
  return `${n} B`;
}

export function Upload({
  label,
  accept,
  maxBytes,
  state,
  progress,
  reason,
  fileName,
  onFile,
  onRemove,
  hint,
  className,
}: UploadProps) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const validate = (file: File): string | null => {
    if (accept.length && !accept.includes(file.type))
      return `That file type is not accepted. Use ${describeAccept(accept)}.`;
    if (file.size > maxBytes)
      return `That file is too large (${formatBytes(file.size)}). The limit is ${formatBytes(maxBytes)}.`;
    return null;
  };
  const pick = (file: File | undefined) => {
    if (!file) return;
    const err = validate(file);
    setLocalError(err);
    if (!err) onFile(file);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    pick(e.dataTransfer.files[0]);
  };
  const busy = state === 'uploading' || state === 'scanning';
  const message = localError ?? reason;
  const describedBy = `${id}-meta ${message ? `${id}-msg` : ''}`.trim();

  return (
    <div
      className={cn('wd-upload', className)}
      data-dragover={dragOver ? 'true' : undefined}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <label htmlFor={id} className="wd-field__label">
        {label}
      </label>
      <p id={`${id}-meta`} className="wd-upload__meta" style={{ margin: 0 }}>
        Accepted: {describeAccept(accept)} · up to {formatBytes(maxBytes)}
        {hint ? ` · ${hint}` : ''}
      </p>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept.join(',')}
        className="wd-sr-only"
        aria-describedby={describedBy}
        aria-invalid={state === 'rejected' || Boolean(localError) ? true : undefined}
        onChange={(e) => pick(e.target.files?.[0])}
        disabled={busy}
      />
      <div className="wd-btn-group">
        <Button
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          disabledReason={busy ? STATE_TEXT[state] : undefined}
        >
          {fileName && (state === 'rejected' || state === 'failed')
            ? 'Choose a different file'
            : fileName
              ? 'Replace file'
              : 'Choose file'}
        </Button>
        {fileName && onRemove && !busy ? (
          <Button variant="quiet" onClick={onRemove}>
            Remove
          </Button>
        ) : null}
      </div>
      {fileName || state !== 'idle' ? (
        <div className="wd-upload__file">
          {fileName ? <span>{fileName}</span> : null}
          <span className="wd-upload__state" data-state={state} role="status">
            {STATE_TEXT[state]}
            {state === 'uploading' && progress != null ? ` · ${Math.round(progress)}%` : ''}
          </span>
          {state === 'uploading' ? (
            <progress
              className="wd-progress__track"
              value={progress ?? 0}
              max={100}
              aria-label="Upload progress"
              style={{ width: '100%' }}
            />
          ) : null}
        </div>
      ) : null}
      {message ? (
        <p id={`${id}-msg`} className="wd-field__error" role="alert" style={{ margin: 0 }}>
          {message}
        </p>
      ) : null}
    </div>
  );
}
